/**
 * Utility functions for export functionality
 */

import { LEIRecord } from '../services/searchService';

// Comprehensive CSV headers in the required order (matching backend specification)
export const CSV_HEADERS = [
    'lei',
    'registered_name',
    'legal_form',
    'legal_address_1',
    'legal_address_2',
    'legal_address_3',
    'legal_address_4',
    'legal_city',
    'legal_region',
    'legal_postal_code',
    'legal_country_code',
    'headquarter_address_1',
    'headquarter_address_2',
    'headquarter_address_3',
    'headquarter_address_4',
    'headquarter_city',
    'headquarter_region',
    'headquarter_postal_code',
    'headquarter_country_code',
    'business_registry_name',
    'business_registry_identifier',
    'lou',
    'entity_status_code',
    'entity_website_address',
    'entity_termination_date',
    'lei_assignment_date',
    'lei_record_last_update',
    'lei_expired_date',
    'lei_next_renewal_date',
    'lei_next_certification_date',
    'lei_expired_reason_code',
    'lei_certification_status',
    'lei_status_code',
    'created_at',
    'updated_at',
    'retrieved_at',
    'other_names',
    'redirected_lei',
    'redirected_lei_reason',
    'redirected_lei_effective_date',
    'legal_jurisdiction',
    'meta_entity_status',
    'meta_lei_registration_status',
    'inferred_jurisdiction_name'
];

/**
 * Extract all required fields from a LEI record in the correct order
 */
export const extractRecordData = (record: LEIRecord): string[] => {
    // Handle other_names - convert array to string if it exists
    const otherNamesValue = record.other_names ? record.other_names.join('; ') : '';

    return [
        record.lei || '',
        record.registered_name || '',
        record.legal_form || '',
        record.legal_address_line_1 || '',
        record.legal_address_line_2 || '',
        record.legal_address_line_3 || '',
        record.legal_address_line_4 || '',
        record.legal_city || '',
        record.legal_region || '',
        record.legal_postal_code || '',
        record.legal_country_code || '',
        record.headquarters_address_line_1 || '',
        record.headquarters_address_line_2 || '',
        record.headquarters_address_line_3 || '',
        record.headquarters_address_line_4 || '',
        record.headquarters_city || '',
        record.headquarters_region || '',
        record.headquarters_postal_code || '',
        record.headquarters_country_code || '',
        record.business_registry_name || '',
        record.business_registry_identifier || '',
        record.lou || '',
        record.entity_status_code || '',
        '', // entity_website_address - not available in LEIRecord interface
        '', // entity_termination_date - not available in LEIRecord interface
        record.lei_assignment_date || '',
        record.lei_record_last_update || '',
        '', // lei_expired_date - not available in LEIRecord interface
        record.lei_next_renewal_date || '',
        '', // lei_next_certification_date - not available in LEIRecord interface
        '', // lei_expired_reason_code - not available in LEIRecord interface
        '', // lei_certification_status - not available in LEIRecord interface
        record.lei_status_code || '',
        '', // created_at - not available in LEIRecord interface
        '', // updated_at - not available in LEIRecord interface
        '', // retrieved_at - not available in LEIRecord interface
        otherNamesValue,
        '', // redirected_lei - not available in LEIRecord interface
        '', // redirected_lei_reason - not available in LEIRecord interface
        '', // redirected_lei_effective_date - not available in LEIRecord interface
        record.legal_jurisdiction || '',
        '', // meta_entity_status - not available in LEIRecord interface
        '', // meta_lei_registration_status - not available in LEIRecord interface
        record.inferred_jurisdiction || ''
    ];
};

/**
 * Create a comprehensive record object for JSON export
 */
export const createExportRecord = (record: LEIRecord): Record<string, string> => {
    const fieldValues = extractRecordData(record);
    const exportRecord: Record<string, string> = {};

    CSV_HEADERS.forEach((header, index) => {
        exportRecord[header] = fieldValues[index];
    });

    return exportRecord;
};
